import React from 'react';
import { Form, useField, useForm } from 'react-final-form';
import { ObjectSchema } from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { AnyMessageParams } from 'yup/lib/types';
import { Box, Flex, Link, Text } from '@chakra-ui/react';
import { FormValues } from 'types';
import { getBaseUrl } from 'api';
import { Stepper } from './Stepper';
import { SubmitButton } from './SubmitButton';
import { Page } from '..';

interface SubmitterProps {
  name: string;
}

const AutoSubmitter: React.FC<SubmitterProps> = ({ name }) => {
  const { input } = useField(name);
  const form = useForm();

  const [ignore, setIgnore] = React.useState(!!input.value);

  React.useLayoutEffect(() => {
    if (!ignore) {
      if (!Array.isArray(input.value) && !!input.value) {
        form.submit();
      }
    } else {
      setIgnore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value]);

  return <></>;
};

interface WizardPageProps {
  validationSchema?: ObjectSchema<ObjectShape>;
  name?: keyof FormValues;
  title?: string;
  loading?: boolean;
  onBackClick?: () => void;
}

const WizardPage: React.FC<WizardPageProps> = ({
  children,
  loading,
  onBackClick,
}) => {
  const childs = React.Children.toArray(
    children,
  ) as React.ReactElement<WizardPageProps>[];
  const pageWithBack = childs.map((child) =>
    React.cloneElement(child, {
      onBackClick,
    }),
  );
  return loading ? null : <>{pageWithBack}</>;
};

interface WizardProps {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => Promise<void>;
  onExit?: () => void;
}

const Wizard: React.FC<WizardProps> = (props) => {
  const { initialValues = {}, children, onSubmit, onExit } = props;

  const [page, setPage] = React.useState(0);
  const [wizardValues, setWizardValues] = React.useState(initialValues);

  const childs = React.Children.toArray(
    children,
  ) as React.ReactElement<WizardPageProps>[];

  const pageNames = childs.map((child) => child.props.title);

  const activePage = childs[page].props;
  const prevPage = childs[page - 1]?.props;
  const isLastPage = page === childs.length - 1;

  const next = (values: Partial<FormValues>) => {
    setPage((prev) => Math.min(prev + 1, React.Children.count(children) - 1));
    setWizardValues(values);
  };

  const previous = (values: Partial<FormValues>) => {
    const changes: Partial<FormValues> = {};

    if (activePage.name) {
      changes[activePage.name] = undefined;
    }

    const prevPageName = prevPage?.name;
    if (prevPageName) {
      if (!Array.isArray(values[prevPageName])) {
        changes[prevPageName] = undefined;
      }
    }

    setPage((prev) => Math.max(prev - 1, 0));

    setWizardValues({
      ...values,
      ...changes,
    });
  };

  const validate = async (values: unknown) => {
    const validator = activePage.validationSchema;

    if (validator) {
      try {
        await validator.validate(values, { abortEarly: false });
      } catch (err) {
        const errors = err.inner.reduce(
          (formError: ObjectShape, innerError: AnyMessageParams) => ({
            ...formError,
            [innerError.path]: innerError.message,
          }),
          {},
        );

        return errors;
      }
    }

    return {};
  };

  const handleFormSubmit = (values: FormValues) => {
    if (isLastPage) {
      return onSubmit(values);
    }

    return next(values);
  };

  return (
    <Form
      initialValues={wizardValues}
      validate={validate}
      onSubmit={handleFormSubmit}
    >
      {({ handleSubmit, submitting, hasValidationErrors, values }) => {
        const wizardPage = childs[page];
        const pageName = wizardPage.props.name;
        const onBackClick = prevPage ? () => previous(values) : onExit;
        const wizardPageWithBack = React.cloneElement(wizardPage, {
          onBackClick,
        });
        return (
          <form onSubmit={handleSubmit}>
            <Page title={activePage?.title} onBackClick={onBackClick}>
              <Box>
                <Stepper
                  pageNames={pageNames}
                  pagesCount={childs.length}
                  currentPage={page}
                />
                {pageName && !isLastPage && <AutoSubmitter name={pageName} />}
                {wizardPageWithBack}
              </Box>
              <Flex
                mt={2}
                justify="space-between"
                alignItems="center"
                direction={
                  isLastPage ? { base: 'column', md: 'row' } : 'column'
                }
              >
                {isLastPage && (
                  <Text
                    as="span"
                    color="grayText"
                    textStyle="body2"
                    mb={{ base: 2, md: 0 }}
                  >
                    Оформляя запись, вы соглашаетесь с условиями{' '}
                    <Link
                      color="black"
                      href={`${getBaseUrl()}/booking-widget/client/agreement/${
                        initialValues.widgetId
                      }`}
                      isExternal
                    >
                      Пользовательского Соглашения
                    </Link>
                  </Text>
                )}
                <SubmitButton
                  isLastPage={isLastPage}
                  hasValidationErrors={hasValidationErrors}
                  submitting={submitting}
                  name={activePage.name}
                />
              </Flex>
            </Page>
          </form>
        );
      }}
    </Form>
  );
};

export { Wizard, WizardPage };
